import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { getSingleMymeAPI } from '@myme/api';
import { editMymeModules } from '@myme/logic';
import { Module } from '@myme/utils';
import { useTranslation } from 'src/hooks';
import { blobCreatorPageRoute, mymeEditorFinishStepPageRoute } from 'src/routes/route-list';
import { handleError, toast, ToastType } from 'src/utils';
import { useMymeEditor } from '../context';
import { usePublishMymeMutation } from '../finishStep/usePublishMymeMutation';

interface Params {
  mymeId?: string;
}

interface EditMymeContentMutation {
  blockNavigation?: boolean;
  publishMyme?: boolean;
}

export const useEditMymeContent = (
  modules: Module[],
  setAreModulesEdited: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const { t } = useTranslation();
  const { mymeId }: Params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    dispatchUpdateMymeContent,
    editor: { isCreator, myme }
  } = useMymeEditor();

  const { publishMymeMutation } = usePublishMymeMutation();

  const editMymeContentMutation = useMutation(
    async (_data: EditMymeContentMutation) => {
      const modulesRequestBody = modules.map(module => ({
        id: !module.isNew ? module.id : null,
        name: module.name,
        blobs: module.blobs.map(blob => ({
          id: blob.id,
          activeAfterSeconds: blob.activeAfterSeconds,
          location: blob.location || undefined
        }))
      }));

      modulesRequestBody[0].blobs[0].activeAfterSeconds = null;
      await editMymeModules(mymeId, modulesRequestBody);
      setAreModulesEdited(false);
    },
    {
      onSuccess: (_res, data) => {
        if (!isCreator) {
          toast(ToastType.SUCCESS, t('pages.mymes.editor.edit_toast_success'));
          queryClient.refetchQueries(getSingleMymeAPI.cacheKeys);
        }

        dispatchUpdateMymeContent(modules);

        if (data.publishMyme) {
          publishMymeMutation.mutate({ mymeId: myme.id, finishStep: true });
          return;
        }

        if (isCreator && !data.blockNavigation) {
          navigate(mymeEditorFinishStepPageRoute(mymeId));
        }

        if (data.blockNavigation) {
          navigate(blobCreatorPageRoute);
        }
      },
      onError: err => {
        handleError(err, {
          displayCustomToast: () => toast(ToastType.ERROR, t('errors.mymes.empty_content'))
        });
      }
    }
  );

  return { editMymeContentMutation };
};
